import type { RequestContext } from "../../../application/context/request-context.js";
import type { PmReport } from "../../../domain/ndt/pm-report-domain.js";
import type { ConventionalUsReport } from "../../../domain/ndt/conventional-us-report-domain.js";
import type { BscanReport } from "../../../domain/ndt/bscan-report-domain.js";
import type { BscanReportRepository, ConventionalUsReportRepository, NdtMethodReportRepository, PmReportRepository } from "../../../repositories/operational-repositories.js";

interface StoredReport { id: string; clientId: string; inspectionId?: string | null }

/**
 * Repositório em memória espelhado em localStorage (como os demais do app
 * real — F5 preserva o rascunho), parametrizado pela chave de armazenamento.
 * PM e US-Convencional têm cada um a sua chave e a sua classe.
 */
class InMemoryNdtMethodReportRepository<R extends StoredReport> implements NdtMethodReportRepository<R> {
  private reports = new Map<string, R>();

  constructor(private readonly storageKey: string, private readonly label: string) {
    this.loadFromStorage();
  }

  private loadFromStorage() {
    try {
      if (typeof localStorage !== "undefined") {
        const stored = localStorage.getItem(this.storageKey);
        if (stored) {
          const parsed: R[] = JSON.parse(stored);
          for (const r of parsed) this.reports.set(r.id, r);
        }
      }
    } catch (e) {
      console.warn(`Failed to load ${this.label} reports from storage:`, e);
    }
  }

  private persistStorage() {
    try {
      if (typeof localStorage !== "undefined") {
        localStorage.setItem(this.storageKey, JSON.stringify(Array.from(this.reports.values())));
      }
    } catch (e) {
      // Fotos/croqui em base64 podem estourar a cota — quem salvou precisa saber.
      console.error(`Failed to persist ${this.label} reports to storage:`, e);
      throw e;
    }
  }

  async findById(_context: RequestContext, id: string): Promise<R | null> {
    this.loadFromStorage();
    return this.reports.get(id) || null;
  }

  async findByInspection(_context: RequestContext, inspectionId: string): Promise<R | null> {
    this.loadFromStorage();
    for (const report of this.reports.values()) if (report.inspectionId === inspectionId) return report;
    return null;
  }

  async listByClient(_context: RequestContext, clientId: string): Promise<readonly R[]> {
    this.loadFromStorage();
    return Array.from(this.reports.values()).filter(r => r.clientId === clientId);
  }

  async listAll(): Promise<readonly R[]> {
    this.loadFromStorage();
    return Array.from(this.reports.values());
  }

  async save(_context: RequestContext, report: R): Promise<void> {
    this.reports.set(report.id, { ...report });
    this.persistStorage();
  }
}

export class InMemoryPmReportRepository extends InMemoryNdtMethodReportRepository<PmReport> implements PmReportRepository {
  constructor() { super("pm_reports", "PM"); }
}

export class InMemoryConventionalUsReportRepository extends InMemoryNdtMethodReportRepository<ConventionalUsReport> implements ConventionalUsReportRepository {
  constructor() { super("usc_reports", "US-Convencional"); }
}

export class InMemoryBscanReportRepository extends InMemoryNdtMethodReportRepository<BscanReport> implements BscanReportRepository {
  constructor() { super("bscan_reports", "B-Scan / C-Scan"); }
}
